/**
 * UI primitives.
 *
 * Accessibility is load-bearing here, not polish: this is a public-sector
 * product. Every interactive element is reachable by keyboard, every state
 * change is announced, and no colour carries meaning on its own.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react'

function juntar(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ')
}

// ---------------------------------------------------------------- Botão ----

type VarianteBotao = 'primario' | 'secundario' | 'perigo' | 'fantasma'
type TamanhoBotao = 'sm' | 'md' | 'lg'

const VARIANTES: Record<VarianteBotao, string> = {
  primario: 'bg-marca-600 text-white hover:bg-marca-700 active:bg-marca-800 shadow-[var(--shadow-suave)]',
  secundario: 'bg-white text-slate-700 border border-[color:var(--color-borda)] hover:bg-slate-50 active:bg-slate-100 shadow-[var(--shadow-suave)]',
  perigo: 'bg-[color:var(--color-perigo)] text-white hover:brightness-110 active:brightness-95',
  fantasma: 'text-slate-600 hover:bg-slate-100 active:bg-slate-200',
}

const TAMANHOS: Record<TamanhoBotao, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
}

export function Botao({
  variante = 'primario',
  tamanho = 'md',
  carregando = false,
  className,
  children,
  disabled,
  ...props
}: {
  variante?: VarianteBotao
  tamanho?: TamanhoBotao
  carregando?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      disabled={disabled || carregando}
      // aria-busy avisa o leitor de tela que a ação está em curso; sem isso o
      // usuário cego não percebe que o clique foi registrado.
      aria-busy={carregando || undefined}
      className={juntar(
        'inline-flex items-center justify-center rounded-lg font-medium transition',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTES[variante],
        TAMANHOS[tamanho],
        className,
      )}
    >
      {carregando && (
        <svg className="size-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      )}
      {children}
    </button>
  )
}

// ----------------------------------------------------------------- Campo ----

export function Campo({
  rotulo,
  ajuda,
  erro,
  obrigatorio,
  className,
  ...props
}: {
  rotulo: string
  ajuda?: string
  erro?: string
  obrigatorio?: boolean
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const id = useId()
  const idAjuda = `${id}-ajuda`
  const idErro = `${id}-erro`

  return (
    <div>
      <label htmlFor={id} className="rotulo">
        {rotulo}
        {obrigatorio && (
          <span className="ml-1 text-[color:var(--color-perigo)]" aria-label="obrigatório">
            *
          </span>
        )}
      </label>
      <input
        {...props}
        id={id}
        aria-invalid={erro ? true : undefined}
        aria-describedby={juntar(ajuda && idAjuda, erro && idErro) || undefined}
        className={juntar('campo', erro && 'campo-erro', className)}
      />
      {ajuda && !erro && (
        <p id={idAjuda} className="ajuda">
          {ajuda}
        </p>
      )}
      {/* role="alert" faz o leitor de tela anunciar o erro assim que aparece. */}
      {erro && (
        <p id={idErro} role="alert" className="mt-1.5 text-xs text-[color:var(--color-perigo)]">
          {erro}
        </p>
      )}
    </div>
  )
}

export function AreaTexto({
  rotulo,
  ajuda,
  className,
  ...props
}: { rotulo: string; ajuda?: string } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId()
  return (
    <div>
      <label htmlFor={id} className="rotulo">
        {rotulo}
      </label>
      <textarea {...props} id={id} className={juntar('campo min-h-24 resize-y', className)} />
      {ajuda && <p className="ajuda">{ajuda}</p>}
    </div>
  )
}

export function Selecao({
  rotulo,
  ajuda,
  opcoes,
  className,
  ...props
}: {
  rotulo: string
  ajuda?: string
  opcoes: Array<{ valor: string; texto: string }>
} & React.SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId()
  return (
    <div>
      <label htmlFor={id} className="rotulo">
        {rotulo}
      </label>
      <select {...props} id={id} className={juntar('campo', className)}>
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.texto}
          </option>
        ))}
      </select>
      {ajuda && <p className="ajuda">{ajuda}</p>}
    </div>
  )
}

export function Alternador({
  rotulo,
  ajuda,
  checked,
  onChange,
  disabled,
}: {
  rotulo: string
  ajuda?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  const id = useId()
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={juntar(
          'relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full transition',
          'disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'bg-marca-600' : 'bg-slate-300',
        )}
      >
        <span
          className={juntar(
            'absolute top-0.5 size-5 rounded-full bg-white shadow transition-all',
            checked ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
      <div className="min-w-0">
        <label htmlFor={id} className="cursor-pointer text-sm font-medium text-slate-700">
          {rotulo}
        </label>
        {ajuda && <p className="text-xs text-slate-500">{ajuda}</p>}
      </div>
    </div>
  )
}

// --------------------------------------------------------------- Etiqueta ----

type TomEtiqueta = 'neutro' | 'marca' | 'sucesso' | 'atencao' | 'perigo'

const TONS: Record<TomEtiqueta, string> = {
  neutro: 'bg-slate-100 text-slate-700 ring-slate-200',
  marca: 'bg-marca-50 text-marca-700 ring-marca-200',
  sucesso: 'bg-[color:var(--color-sucesso-fundo)] text-[color:var(--color-sucesso)] ring-emerald-200',
  atencao: 'bg-[color:var(--color-atencao-fundo)] text-[color:var(--color-atencao)] ring-amber-200',
  perigo: 'bg-[color:var(--color-perigo-fundo)] text-[color:var(--color-perigo)] ring-red-200',
}

export function Etiqueta({
  tom = 'neutro',
  children,
}: {
  tom?: TomEtiqueta
  children: ReactNode
}) {
  return (
    <span
      className={juntar(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
        TONS[tom],
      )}
    >
      {children}
    </span>
  )
}

// ----------------------------------------------------------------- Cartão ----

export function Cartao({
  titulo,
  descricao,
  acoes,
  children,
  className,
}: {
  titulo?: string
  descricao?: string
  acoes?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={juntar('cartao', className)}>
      {(titulo || acoes) && (
        <header className="flex items-start justify-between gap-4 border-b border-[color:var(--color-borda-suave)] px-5 py-4">
          <div className="min-w-0">
            {titulo && <h2 className="text-base font-semibold text-slate-900">{titulo}</h2>}
            {descricao && <p className="mt-0.5 text-sm text-slate-500">{descricao}</p>}
          </div>
          {acoes && <div className="flex shrink-0 gap-2">{acoes}</div>}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  )
}

// ------------------------------------------------------------------ Modal ----

export function Modal({
  aberto,
  aoFechar,
  titulo,
  descricao,
  children,
  largura = 'md',
}: {
  aberto: boolean
  aoFechar: () => void
  titulo: string
  descricao?: string
  children: ReactNode
  largura?: 'md' | 'lg' | 'xl'
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberto) return

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar()
    }
    document.addEventListener('keydown', aoTeclar)

    // Trava o scroll do fundo enquanto o modal está aberto.
    const overflowAnterior = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Move o foco para dentro do modal — sem isso o Tab continua percorrendo
    // a página atrás, e quem navega por teclado se perde.
    ref.current?.focus()

    return () => {
      document.removeEventListener('keydown', aoTeclar)
      document.body.style.overflow = overflowAnterior
    }
  }, [aberto, aoFechar])

  if (!aberto) return null

  const larguras = { md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
      <div
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={aoFechar}
        aria-hidden="true"
      />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className={juntar(
          'surgir relative my-8 w-full rounded-xl bg-white shadow-[var(--shadow-alta)] outline-none',
          larguras[largura],
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{titulo}</h2>
            {descricao && <p className="mt-0.5 text-sm text-slate-500">{descricao}</p>}
          </div>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="-mr-1 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <svg className="size-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </header>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ Avisos ----

export function Aviso({
  tom = 'info',
  titulo,
  children,
}: {
  tom?: 'info' | 'sucesso' | 'atencao' | 'perigo'
  titulo?: string
  children: ReactNode
}) {
  const estilos = {
    info: 'bg-[color:var(--color-info-fundo)] text-marca-900 border-marca-200',
    sucesso: 'bg-[color:var(--color-sucesso-fundo)] text-emerald-900 border-emerald-200',
    atencao: 'bg-[color:var(--color-atencao-fundo)] text-amber-900 border-amber-200',
    perigo: 'bg-[color:var(--color-perigo-fundo)] text-red-900 border-red-200',
  }

  return (
    <div
      // Erros e alertas são anunciados; info não interrompe a leitura.
      role={tom === 'perigo' ? 'alert' : 'status'}
      className={juntar('rounded-lg border px-4 py-3 text-sm', estilos[tom])}
    >
      {titulo && <p className="mb-0.5 font-semibold">{titulo}</p>}
      <div className="[&_a]:underline">{children}</div>
    </div>
  )
}

// ------------------------------------------------------------------ Tabela ----

export function Tabela({ children }: { children: ReactNode }) {
  return (
    // A tabela rola dentro do próprio container; a página nunca rola na horizontal.
    <div className="overflow-x-auto">
      <table className="w-full text-sm">{children}</table>
    </div>
  )
}

export function Cabecalho({ colunas }: { colunas: string[] }) {
  return (
    <thead>
      <tr className="border-b border-[color:var(--color-borda)]">
        {colunas.map((c) => (
          <th
            key={c}
            scope="col"
            className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-wider text-slate-500 uppercase"
          >
            {c}
          </th>
        ))}
      </tr>
    </thead>
  )
}

export function Vazio({
  titulo,
  descricao,
  acao,
}: {
  titulo: string
  descricao?: string
  acao?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-slate-100">
        <svg className="size-6 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <p className="font-medium text-slate-900">{titulo}</p>
      {descricao && <p className="mt-1 max-w-sm text-sm text-slate-500">{descricao}</p>}
      {acao && <div className="mt-4">{acao}</div>}
    </div>
  )
}

export function Carregando({ texto = 'Carregando…' }: { texto?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 px-6 py-14 text-sm text-slate-500" role="status">
      <svg className="size-5 animate-spin text-marca-600" viewBox="0 0 24 24" aria-hidden="true">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
      {texto}
    </div>
  )
}

/** Formats integer cents as pt-BR currency. */
export function formatarBRL(centavos: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    centavos / 100,
  )
}

export function formatarData(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(iso),
  )
}

export function formatarDataCurta(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [ano, mes, dia] = iso.slice(0, 10).split('-')
  return `${dia}/${mes}/${ano}`
}
